import { getPrices, isPriceRange, type ProductHitPrice } from '@libs/algolia';
import { encodeFref, type FrefListId, type FrefListName, type Product } from '@libs/common-next';
import type { RelatedProductItem } from '@libs/common-types';

export type YmalFrefAttribution = {
    listId: FrefListId;
    listName: FrefListName;
};

const appendFrefToUrl = (url: string, listId: FrefListId, listName: FrefListName, index: number): string => {
    const frefQuery = encodeFref({ listId, listName, index });
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${frefQuery}`;
};

type RelatedProductPriceVariant = 'primary' | 'sale';

/** Length of tuple from {@link getPrices} when both sale and list prices are hyphen ranges. */
const RANGE_WITH_LIST_PRICE_COUNT = 4;
const SALE = 'sale';
const PRICE_COUNT = 2;
const PRIMARY = 'primary';
/**
 * Maps Algolia {@link ProductHitPrice} into carousel `prices` + `isPriceRange`.
 * - Four values = `[saleLow, saleHigh, listLow, listHigh]`; keep only the sale bracket for the tile.
 * - Two values with sale variant (non-range) = `[sale, list]`; show the sale value only.
 */
const resolveYmalProductPriceDisplay = (
    price: ProductHitPrice,
    priceVariant: RelatedProductPriceVariant
): { prices: Array<string | number>; isPriceRange: boolean } => {
    const isPriceRangeFlag = isPriceRange(price);
    const rawPrices = getPrices(price, isPriceRangeFlag);

    if (rawPrices.length === RANGE_WITH_LIST_PRICE_COUNT) {
        return {
            prices: rawPrices.slice(0, 2),
            isPriceRange: true,
        };
    }

    if (priceVariant === SALE && rawPrices.length === PRICE_COUNT) {
        return {
            prices: rawPrices.slice(0, 1),
            isPriceRange: false,
        };
    }

    return { prices: rawPrices, isPriceRange: isPriceRangeFlag };
};

export const transformYmalProductForDisplay = (
    product: RelatedProductItem,
    index: number,
    frefAttribution?: YmalFrefAttribution
) => {
    const priceVariant: RelatedProductPriceVariant = product.price.formatted.strikethrough ? SALE : PRIMARY;
    const price = product.price;
    const { prices, isPriceRange } = resolveYmalProductPriceDisplay(price, priceVariant);
    const href = frefAttribution
        ? appendFrefToUrl(product.url, frefAttribution.listId, frefAttribution.listName, index + 1)
        : product.url;
    return {
        __position: index,
        sku: product.sku,
        title: product.name,
        description: product.short_description,
        href,
        target: '_self' as const,
        imgSrc: product.image,
        imgAlt: product.name,
        brand: product.brand,
        priceVariant,
        prices,
        isPriceRange: isPriceRange,
        outOfStock: false,
        rating: product.bvAverageRating,
        ratingLabel: product.bvReviewCount ? `(${product.bvReviewCount})` : '(0)',
    };
};

export const mapYmalProductToAnalyticsCategories = (product?: RelatedProductItem) => [
    product?.category ?? '',
    product?.sub_category1 ?? '',
    product?.sub_category2 ?? '',
];

export const mapYmalProductsToAnalyticsProducts = (products: RelatedProductItem[]): Product[] => {
    return products.map((product, index) => {
        const productId = parseInt(product.sku, 10) || 0;
        const position = index + 1;

        return {
            id: productId,
            title: product.name,
            __position: position,
            objectID: product.sku,
            brand: product.brand,
            prices: [product.price.number.toString()],
            merchandisingHierarchy: product.merchandisingHierarchy,
            masterProductID: product.masterProductID,
            rawProductHit: {
                __position: position,
                id: productId,
                objectID: product.sku,
            },
            itemSize: product.size,
            color: product.color
                ? {
                      families: '',
                      swatchDisplay: '',
                      value: product.color,
                  }
                : undefined,
            flavor: product.flavor
                ? {
                      displays: product.flavor,
                      value: product.flavor,
                  }
                : undefined,
        };
    });
};
